import React, {useState} from 'react';
import {flowRight as compose, orderBy} from 'lodash';
import PropTypes from 'prop-types';
import PopIn from '../../../common/components/PopIn';
import {withTranslation} from 'react-i18next';
import {CancelButton, LoadingContainer, TitleButton} from '../../../common/components/StyledComp';
import {Mutation, Query, useQuery} from 'react-apollo';
import gql from 'graphql-tag';
import {PulseLoader} from 'react-spinners';
import {Card, Col, Container, ListGroup, Row} from 'react-bootstrap';
import styled from 'styled-components';
import {Link} from 'react-router-dom';
import SubscriptionPlanInfo from './SubscriptionPlanInfo';
import {dateFormatFR} from '../../../utils';
import Error from '../../../common/components/Error/Error';
import {BILLING_SUBSCRIPTION_CANCEL_AT} from '../../../common/constants';
import {withRouter} from 'react-router';

const CenteredDiv = styled.div`
  text-align: center;
`;

const CancelRow = styled(Row)`
  margin-top: 30px;
  margin-bottom: 10px;
  text-align: center;
`;

const GRAPHQL_QUERY_BILLING_SUBSCRIPTION_PLANS = gql`
  query billingSubscriptionPlans($criteria: CriteriaListInput, $additionalCriteria: BillingSubscriptionPlansCriteriaListInput) {
    billingSubscriptionPlans(criteria: $criteria, additionalCriteria: $additionalCriteria) {
      rows {
        id
        amount
        billingScheme
        currency
        interval
        intervalCount
        nickname
        position
        tiers {
          id
          flatAmount
          unitAmount
          upTo
        }
      }
      hasMore
      id
    }
  }
`;

const GRAPHQL_QUERY_USER_ACTIVE_SUBSCRIPTION = gql`
  query userActiveSubscription($userId: ID!) {
    user(id: $userId) {
      id
      role
      hasActiveSubscription
      activeSubscription {
        id
        plan {
          id
          amount
          billingScheme
          currency
          interval
          intervalCount
          nickname
          position
          tiers {
            id
            flatAmount
            unitAmount
            upTo
          }
        }
        collectionMethod
        created
        currentPeriodEnd
        currentPeriodStart
        cancelAtPeriodEnd
        canceledAt
      }
    }
  }
`;

const GRAPHQL_QUERY_CANCEL_SUBSCRIPTION = gql`
  mutation cancelSubscription($userId: ID!, $cancelAt: BillingSubscriptionCancelAt!) {
    cancelSubscription(userId: $userId, cancelAt: $cancelAt) {
      id
      plan {
        id
        amount
        interval
        intervalCount
      }
      collectionMethod
      created
      currentPeriodEnd
      currentPeriodStart
      cancelAtPeriodEnd
      canceledAt
    }
  }
`;

const SubscriptionManagement = ({t, userId, allowCancelSubscription, currentUsage, location, history, displaySubscriptionModal}) => {
  const [displaySubscriptionForm, setDisplaySubscriptionForm] = useState(displaySubscriptionModal);
  const searchParams = new URLSearchParams(location.search);
  const [displaySubscriptionSuccess, setDisplaySubscriptionSuccess] = useState(searchParams.has('newSubscription'));
  const [displaySubscriptionCancelModal, setDisplaySubscriptionCancelModal] = useState(false);

  const onDisplaySubscriptionSuccess = visible => {
    if (!visible && searchParams.has('newSubscription')) {
      searchParams.delete('newSubscription');
    }
    history.replace(location.pathname + '?' + searchParams.toString() + location.hash);
    setDisplaySubscriptionSuccess(visible);
  };

  const {loading, error, data} = useQuery(GRAPHQL_QUERY_USER_ACTIVE_SUBSCRIPTION, {
    variables: {userId},
    fetchPolicy: 'cache-and-network',
  });

  if (loading) {
    return <PulseLoader/>;
  }
  if (error) {
    console.error(error);
    return null;
  }

  const user = data && data.user;

  let content;
  if (user.hasActiveSubscription) {
    let subscription = data && data.user && data.user.activeSubscription;
    if (!subscription) {
      content = (
        <CenteredDiv>
          <p>{t('subscriptionManagement.noInformation')}</p>
        </CenteredDiv>
      );
    } else {
      const subscriptionIsCanceled = subscription.cancelAtPeriodEnd || subscription.canceledAt;
      let cancelActionContent = null;
      if (allowCancelSubscription && !subscriptionIsCanceled) {
        cancelActionContent = (
          <Mutation
            mutation={GRAPHQL_QUERY_CANCEL_SUBSCRIPTION}
            onCompleted={() => setDisplaySubscriptionCancelModal(false)}
            refetchQueries={['userActiveSubscription']}
            awaitRefetchQueries
          >
            {(cancelMutation, {loading, error}) => (
              <>
                <CancelButton type="button" onClick={() => setDisplaySubscriptionCancelModal(true)} style={{marginTop: 0, width: 'auto'}}>{t('subscriptionManagement.cancelSubscription')}</CancelButton>
                <PopIn
                  title={t('subscriptionManagement.cancelSubscription')}
                  visible={displaySubscriptionCancelModal}
                  setModalVisible={setDisplaySubscriptionCancelModal}
                  size={'lg'}
                >
                  {loading ? (
                    <LoadingContainer>
                      <PulseLoader/>
                    </LoadingContainer>
                  ) : (
                    <>
                      {t('subscriptionManagement.cancelSubscriptionContent', {endDate: dateFormatFR({date: subscription.currentPeriodEnd})})}
                      <Error error={error}/>
                      <CancelRow>
                        <Col className="text-right">
                          <CancelButton onClick={() => cancelMutation({variables: {userId, cancelAt: BILLING_SUBSCRIPTION_CANCEL_AT.NOW}})} style={{marginTop: 0, width: 'auto'}}>
                            {t('subscriptionManagement.cancelNowBtn')}
                          </CancelButton>
                        </Col>
                        <Col className="text-left">
                          <CancelButton onClick={() => cancelMutation({variables: {userId, cancelAt: BILLING_SUBSCRIPTION_CANCEL_AT.PERIOD_END}})} style={{marginTop: 0, width: 'auto'}}>
                            {t('subscriptionManagement.cancelAtPeriodEndBtn')}
                          </CancelButton>
                        </Col>
                      </CancelRow>
                      <CancelRow>
                        <Col sm={{span: 6, offset: 3}}>
                          <TitleButton onClick={() => setDisplaySubscriptionCancelModal(false)}>
                            {t('subscriptionManagement.cancelBtn')}
                          </TitleButton>
                        </Col>
                      </CancelRow>
                    </>
                  )}
                </PopIn>
              </>
            )}
          </Mutation>
        );
      }

      content = (
        <Row className={'align-items-center'}>
          <Col sm={12} md={6} className={'p-1'}>
            <Card className={'mb-0'}>
              <Card.Body className={'p-1'}>
                <SubscriptionPlanInfo subscriptionPlan={subscription.plan} currentUsage={currentUsage}/>
              </Card.Body>
            </Card>
          </Col>
          <Col sm={12} md={6} className={'p-1'}>
            <Card className={'mb-0'}>
              <Card.Body className={'p-1'}>
                <ListGroup variant={'flush'}>
                  <ListGroup.Item className={'p-1'}>{t('subscriptionManagement.subscribeOn')}: {dateFormatFR({date: subscription.created})}</ListGroup.Item>
                  <ListGroup.Item className={'p-1'}>{t('subscriptionManagement.currentPeriodStart')}: {dateFormatFR({date: subscription.currentPeriodStart})}</ListGroup.Item>
                  <ListGroup.Item className={'p-1'}>{t('subscriptionManagement.currentPeriodEnd')}: {dateFormatFR({date: subscription.currentPeriodEnd})}</ListGroup.Item>
                </ListGroup>
              </Card.Body>
            </Card>
          </Col>
          {(subscriptionIsCanceled || cancelActionContent) && (
            <Col xs={12}>
              <div className="text-center mt-2">
                {subscriptionIsCanceled && t('subscriptionManagement.cancelAt', {
                  endDate: dateFormatFR({
                    date: subscription.cancelAtPeriodEnd ? subscription.currentPeriodEnd : subscription.cancelAt,
                  }),
                })}
                {cancelActionContent}
              </div>
            </Col>
          )}
        </Row>
      );
    }
  } else {
    content = (
      <CenteredDiv>
        <p>{t('subscriptionManagement.getSubscription')}</p>
        <div className="popin-buttons">
          <TitleButton onClick={() => setDisplaySubscriptionForm(true)}>{t('subscribe')}</TitleButton>
        </div>
      </CenteredDiv>
    );
  }

  const additionalCriteria = {
    role: user.role,
  };
  return (
    <Container fluid className={'p-1'}>
      {content}

      <PopIn title={t('subscriptionManagement.subscription')} visible={displaySubscriptionForm} setModalVisible={setDisplaySubscriptionForm} size={'lg'}>
        <Query query={GRAPHQL_QUERY_BILLING_SUBSCRIPTION_PLANS} variables={{additionalCriteria}}>
          {({data: dataPlans, loading: loadingPlans, error: errorPlans}) => {
            if (loadingPlans) {
              return (
                <LoadingContainer>
                  <PulseLoader/>
                </LoadingContainer>
              );
            }
            if (errorPlans) {
              console.error(errorPlans);
              return null;
            }
            const subscriptionPlans = orderBy(dataPlans.billingSubscriptionPlans.rows, ['position'], ['asc']) || [];
            return (
              <Row>
                {subscriptionPlans.map((subscriptionPlan, index) => (
                  <Col key={index}>
                    <Card>
                      <Card.Body>
                        <Card.Title>{subscriptionPlan.nickname}</Card.Title>
                        <SubscriptionPlanInfo subscriptionPlan={subscriptionPlan}/>
                        <div className="popin-buttons">
                          <Link to={{
                            pathname: '/admin/user/' + userId + '/subscribe/' + subscriptionPlan.id,
                            state: {
                              oldLocation: location,
                            },
                          }}>
                            <TitleButton>{t('subscriptionManagement.choose')}</TitleButton>
                          </Link>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            );
          }}
        </Query>
      </PopIn>

      <PopIn title={t('newSubscription.title')} visible={displaySubscriptionSuccess} setModalVisible={onDisplaySubscriptionSuccess} size={'md'}>
        {t('newSubscription.body')}
        <div className="popin-buttons mt-3">
          <TitleButton onClick={() => onDisplaySubscriptionSuccess(false)}>
            {t('newSubscription.ok')}
          </TitleButton>
        </div>
      </PopIn>
    </Container>
  );
};

SubscriptionManagement.defaultProps = {
  allowCancelSubscription: false,
  currentUsage: null,
  displaySubscriptionModal: false,
};

SubscriptionManagement.propTypes = {
  t: PropTypes.func.isRequired,
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  allowCancelSubscription: PropTypes.bool,
  currentUsage: PropTypes.number,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string,
    hash: PropTypes.string,
  }).isRequired,
  history: PropTypes.shape({
    replace: PropTypes.func.isRequired,
  }).isRequired,
  displaySubscriptionModal: PropTypes.bool,
};

export default compose(
  withTranslation('stripe'),
  withRouter,
)(SubscriptionManagement);
